import React, { useState, useEffect } from 'react';
import { supabase, conversations, messages, notifications } from '@/integrations/supabase/client';
import { Conversation, Message, Notification } from '@/types/business';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bell, Send, Paperclip, MoreVertical } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ConversationsTabProps {
  userId: string;
  userRole: 'investor' | 'entrepreneur' | 'admin';
}

export const ConversationsTab: React.FC<ConversationsTabProps> = ({ userId, userRole }) => {
  const [conversationsList, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messagesList, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [notificationsList, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConversations();
    fetchNotifications();
    // Subscribe to new messages
    const subscription = supabase
      .channel('messages')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, handleNewMessage)
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [userId]);

  const fetchConversations = async () => {
    try {
      // For admin users, fetch all conversations they're part of
      // For other users, fetch conversations where they're a participant
      const { data, error } = await conversations()
        .select('*')
        .or(`participants.cs.{${userId}},conversation_type.eq.admin_${userRole}`)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      setConversations(data || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    try {
      const { data, error } = await messages()
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const { data, error } = await notifications()
        .select('*')
        .eq('user_id', userId)
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleNewMessage = (payload: any) => {
    const newMessage = payload.new as Message;
    if (selectedConversation?.id === newMessage.conversation_id) {
      setMessages(prev => [...prev, newMessage]);
    }
    fetchConversations();
    fetchNotifications();
  };

  const sendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;

    setLoading(true);
    try {
      // Create message
      const { data: message, error: messageError } = await messages()
        .insert([{
          conversation_id: selectedConversation.id,
          sender_id: userId,
          content: newMessage.trim(),
          read_by: [userId]
        }])
        .select()
        .single();

      if (messageError) throw messageError;

      // Update conversation's last_message_at
      await conversations()
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', selectedConversation.id);

      // Create notifications for other participants
      for (const participantId of selectedConversation.participants) {
        if (participantId !== userId) {
          await notifications()
            .insert([{
              user_id: participantId,
              type: 'conversation_reply',
              title: 'New Message',
              content: `New message in conversation: ${selectedConversation.title}`,
              read: false,
              link: `/conversations/${selectedConversation.id}`,
              metadata: {
                conversation_id: selectedConversation.id,
                message_id: message.id,
                sender_id: userId
              }
            }]);
        }
      }

      setNewMessage('');
      setMessages(prev => [...prev, message]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (conversationId: string) => {
    try {
      // First get the current messages
      const { data: conversationMessages } = await messages()
        .select('id, read_by')
        .eq('conversation_id', conversationId)
        .neq('sender_id', userId);

      if (!conversationMessages) return;

      // Update each message with the new read_by array
      for (const message of conversationMessages) {
        const readBy = message.read_by || [];
        if (!readBy.includes(userId)) {
          await messages()
            .update({ read_by: [...readBy, userId] })
            .eq('id', message.id);
        }
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Conversations List */}
      <div className="w-1/3 border-r">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Conversations</h2>
            <Button variant="ghost" size="icon">
              <Bell className="h-5 w-5" />
            </Button>
          </div>
        </div>
        <ScrollArea className="h-[calc(100vh-8rem)]">
          {conversationsList.map((conversation) => (
            <div
              key={conversation.id}
              className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                selectedConversation?.id === conversation.id ? 'bg-blue-50' : ''
              }`}
              onClick={() => {
                setSelectedConversation(conversation);
                fetchMessages(conversation.id);
                markAsRead(conversation.id);
              }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-medium">{conversation.title}</h3>
                  <p className="text-sm text-gray-500">
                    {formatDistanceToNow(new Date(conversation.last_message_at), { addSuffix: true })}
                  </p>
                </div>
                {notificationsList.some(n => n.metadata?.conversation_id === conversation.id) && (
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                )}
              </div>
            </div>
          ))}
        </ScrollArea>
      </div>

      {/* Messages Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            <div className="p-4 border-b">
              <h2 className="text-lg font-semibold">{selectedConversation.title}</h2>
            </div>
            <ScrollArea className="flex-1 p-4">
              {messagesList.map((message) => (
                <div
                  key={message.id}
                  className={`mb-4 ${
                    message.sender_id === userId ? 'text-right' : 'text-left'
                  }`}
                >
                  <div
                    className={`inline-block p-3 rounded-lg ${
                      message.sender_id === userId
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100'
                    }`}
                  >
                    <p>{message.content}</p>
                    <p className="text-xs mt-1 opacity-70">
                      {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </ScrollArea>
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                />
                <Button onClick={sendMessage} disabled={loading}>
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Select a conversation to start messaging
          </div>
        )}
      </div>
    </div>
  );
}; 